import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import {
  type PersonDetailFormValues,
  type PersonDetailValidationErrors,
} from '@v2/domains/person/types/person-detail.types'

export interface HeaderDisplayValues {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
}

export interface PersonDetailFormState {
  values: PersonDetailFormValues
  errors: Partial<Record<keyof PersonDetailFormValues, string>>
  organizationId: string
  skipDobValidation: boolean
  displayValues: HeaderDisplayValues
}

export const EMPTY_PERSON_DETAIL_FORM_VALUES: PersonDetailFormValues = {
  id: '',
  email: '',
  phoneNumber: '',
  otherPhoneNumber: [],
  firstName: '',
  lastName: '',
  gender: '',
  otherGender: '',
  birthDate: '',
  preferredLanguage: '',
  otherPreferredLanguage: '',
  street: '',
  city: '',
  state: '',
  postalCode: '',
  companies: [],
  source: undefined,
  haveHealthInsurance: '',
  coverageThroughTheState: '',
  pay20PerMonth: '',
  blackCarFund: '',
  rating: '',
  updatedAt: '',
  createdAt: '',
  otherEmail: [],
  createdBy: {},
  updatedBy: {},
}

const getDisplayValues = (
  values: PersonDetailFormValues,
): HeaderDisplayValues => ({
  firstName: values.firstName,
  lastName: values.lastName,
  email: values.email,
  phoneNumber: values.phoneNumber,
})

const initialState: PersonDetailFormState = {
  values: EMPTY_PERSON_DETAIL_FORM_VALUES,
  errors: {},
  organizationId: '',
  skipDobValidation: false,
  displayValues: getDisplayValues(EMPTY_PERSON_DETAIL_FORM_VALUES),
}

const personDetailFormSlice = createSlice({
  name: 'personDetailForm',
  initialState,
  reducers: {
    initializeForm(
      state,
      action: PayloadAction<{
        values: PersonDetailFormValues
        organizationId: string
      }>,
    ) {
      state.values = action.payload.values
      state.errors = {}
      state.organizationId = action.payload.organizationId
      state.skipDobValidation = false
      state.displayValues = getDisplayValues(action.payload.values)
    },

    setFieldValue(
      state,
      action: PayloadAction<{
        field: keyof PersonDetailFormValues
        value: PersonDetailFormValues[keyof PersonDetailFormValues]
      }>,
    ) {
      const { field, value } = action.payload
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(state.values as Record<string, any>)[field] = value
      if (state.errors[field]) {
        delete state.errors[field]
      }
    },

    setFormErrors(state, action: PayloadAction<PersonDetailValidationErrors>) {
      state.errors = action.payload
    },

    cancelEdit(state, action: PayloadAction<PersonDetailFormValues>) {
      state.values = action.payload
      state.errors = {}
      state.displayValues = getDisplayValues(action.payload)
    },

    setSkipDobValidation(state, action: PayloadAction<boolean>) {
      state.skipDobValidation = action.payload
    },

    syncDisplayValues(state) {
      state.displayValues = getDisplayValues(state.values)
    },

    resetForm() {
      return initialState
    },
  },
})

export const {
  initializeForm,
  setFieldValue,
  setFormErrors,
  cancelEdit,
  setSkipDobValidation,
  syncDisplayValues,
  resetForm,
} = personDetailFormSlice.actions

export default personDetailFormSlice.reducer
